import React, { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { ArrowLeft, ChevronDown, Check, Trash2, X } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

/**
 * MaterialSelectionModal copied from CreateMaterialRequest.js
 * Allows selecting multiple materials from org library and returns selections
 */
function MaterialSelectionModal({
  isOpen,
  onClose,
  onSelectMaterial,
  selectedMaterials = [],
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const organisationId = JSON.parse(localStorage.getItem("organization"))?.id;
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const getMaterials = async () => {
    const res = await fetch(`${BASE_URL}/org/material-library/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ org_id: organisationId }),
    });
    const data = await res.json();
    setAvailableMaterials(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      getMaterials();
    }
  }, [isOpen]);

  const filteredMaterials = availableMaterials.filter((material) => {
    const name = (material?.material_name || "").toLowerCase();
    const sub = (material?.subcategory || "").toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || sub.includes(q);
  });

  const handleMaterialToggle = (material, alreadyInRequest) => {
    if (alreadyInRequest) return;
    setSelectedIds((prev) =>
      prev.includes(material.id)
        ? prev.filter((id) => id !== material.id)
        : [...prev, material.id],
    );
  };

  const handleAddSelected = () => {
    if (!selectedIds.length) return;
    const toAdd = availableMaterials.filter((m) => selectedIds.includes(m.id));
    toAdd.forEach((m) => onSelectMaterial(m));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-[37vw] bg-white rounded-2xl px-3 py-2 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex flex-row items-center py-2 justify-between border-b-[0.5px] border-[#A3A3A3]">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            <X size={20} />
          </button>

          <div className="text-lg font-normal flex flex-row items-center  text-[#232323]">
            Select Material
          </div>
          <div className=" flex w-[10%] flex-col items-center"></div>
        </div>

        <div className="flex-1 overflow-hidden py-2 flex flex-col">
          <div className="pb-2 mt-2 flex flex-col w-full items-left">
            <span className="text-sm font-normal pl-1 w-full text-[#232323]">
              Select from your material inventory
            </span>
            <div className="relative w-full flex flex-col items-left">
              <input
                type="text"
                placeholder="Search Your Material"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className=" pl-10 pr-4 py-1 w-[97%] ml-[2px] mt-1 text-base bg-[#F3F4F6] rounded-lg border-0 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            <div className="space-y-1">
              {filteredMaterials.map((material) => {
                const alreadyInRequest = selectedMaterials.some(
                  (s) => s.id === material.id,
                );
                const isNewlySelected = selectedIds.includes(material.id);
                const isSelected = alreadyInRequest || isNewlySelected;
                return (
                  <button
                    key={material.id}
                    onClick={() =>
                      handleMaterialToggle(material, alreadyInRequest)
                    }
                    className={`w-full flex items-center gap-3 px-2 py-1 border-b-[0.5px] border-[#C3C3C3] text-left transition
                      ${alreadyInRequest
                        ? "cursor-not-allowed opacity-70"
                        : isNewlySelected
                          ? "bg-purple-50"
                          : "hover:bg-gray-50"
                      }`}
                    disabled={alreadyInRequest}
                    aria-disabled={alreadyInRequest}
                    title={
                      alreadyInRequest
                        ? "Already added"
                        : isNewlySelected
                          ? "Click to remove"
                          : "Click to select"
                    }
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${alreadyInRequest
                          ? "bg-gray-300 border-gray-300"
                          : isNewlySelected
                            ? "bg-[#3B0069] border-[#3B0069]"
                            : "border-[#232323]"
                        }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#232323]">
                        {material.material_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {material?.specification && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.specification}
                        </span>
                      )}
                      {material?.subcategory && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.subcategory}
                        </span>
                      )}
                      {material?.uom && (
                        <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                          {material.uom}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredMaterials.length === 0 && (
                <div className="text-sm text-gray-500 p-4">
                  No materials found.
                </div>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {selectedIds.length
                ? `${selectedIds.length} material${selectedIds.length > 1 ? "s" : ""
                } selected`
                : "Select one or more materials to add"}
            </span>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.length === 0}
              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3B0069] disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#3B0069D9] transition-colors"
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RequestForQuoatation({ onClose, onCreateRFQ }) {
  const [project, setProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [isWarehouse, setIsWarehouse] = useState(false);
  const [materialLibrary, setMaterialLibrary] = useState([]);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(null);

  const [materials, setMaterials] = useState([]);
  const { addAlert } = useAlert();
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const addMaterial = () => {
    // Open modal to add one or more materials from library
    setShowMaterialModal(true);
  };

  const handleSelectMaterial = (material) => {
    // Add selected material if not already present
    setMaterials((prev) => {
      if (prev.some((m) => m.materialId === material.id)) return prev;
      return [
        ...prev,
        {
          materialId: material.id,
          name: material.material_name,
          specification: material.specification,
          subcategory: material.subcategory,
          brand: material.brand_name,
          qty: "",
          unit: material.uom,
        },
      ];
    });
  };

  const updateMaterial = (index, updates) => {
    setMaterials((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    );
  };

  const removeMaterial = (index) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const selectProject = (proj) => {
    setProject(proj);
    setShowProjectDropdown(false);
  };

  const selectMaterial = (index, material) => {
    updateMaterial(index, {
      materialId: material.id,
      name: material.material_name,
      specification: material.specification,
      subcategory: material.subcategory,
      brand: material.brand_name,
      unit: material.uom,
    });
    setShowMaterialDropdown(null);
  };

  const getProjects = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    const res = await fetch(`${BASE_URL}/sites/organizations/${orgId}/sites/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
    const data = await res.json();
    setProjects(Array.isArray(data) ? data : []);
  };

  const getMaterialLibrary = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    const res = await fetch(`${BASE_URL}/org/material-library/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
      body: JSON.stringify({ org_id: orgId }),
    });
    const data = await res.json();
    setMaterialLibrary(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    getProjects();
    getMaterialLibrary();
  }, []);

  const raiseRFQ = async () => {
    // Validation: Check if at least one material is added
    if (materials.length === 0) {
      addAlert("Please add at least one material", "error");
      return;
    }

    // Validation: Check if all materials are selected (not default "Select Material")
    const hasUnselectedMaterial = materials.some((m) => m.materialId === null);
    if (hasUnselectedMaterial) {
      addAlert("Please select all materials from the dropdown", "error");
      return;
    }

    // Validation: Check if all materials have quantity > 0
    const hasInvalidQuantity = materials.some((m) => m.qty <= 0);
    if (hasInvalidQuantity) {
      addAlert("All materials must have quantity greater than 0", "error");
      return;
    }

    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;

    const payload = {
      organization: orgId,
      sites: isWarehouse ? null : project.id,
      is_warehouse_request: isWarehouse,
      lines: materials.map((m) => ({
        material: m.materialId,
        quantity: m.qty,
      })),
    };

    try {
      const res = await fetch(`${BASE_URL}/inventory/rfq/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to raise RFQ");
      }

      const data = await res.json();
      console.log("RFQ Created:", data);
      addAlert("RFQ created successfully", "success");
      onCreateRFQ?.(data);
      onClose(); // close modal after success
    } catch (err) {
      console.error(err);
      addAlert("Error creating RFQ", "error");
    }
  };

  return (
    <div className="absolute inset-0 bg-gray-100 flex flex-col h-full">
      <div className="flex-shrink-0 flex justify-between items-center p-4 rounded-lg m-2 mb-0">
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center text-[#232323]"
        >
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-base font-semibold text-gray-900">
          Request for Quotation
        </h2>
        <div className="w-8" />
      </div>

      <div className=" bg-white rounded-xl mx-16 mt-4 py-6 px-4">

        {/* <div className="flex justify-start gap-2">
          <div className="text-sm flex items-center text-[#232323] mb-1">
            Request for Quotation No.
          </div>
          <div className="inline-flex items-center gap-2 bg-gray-200 px-2 rounded-full text-sm font-medium text-[#232323]">
            RFQ_ID_01
          </div>
        </div> */}
        {/* Is warehouse checkbox */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isWarehouse"
            checked={isWarehouse}
            onChange={() => setIsWarehouse(!isWarehouse)}
            className="h-4 w-4 text-[#3B0069] border-gray-300 rounded"
          />
          <label htmlFor="isWarehouse" className="text-sm text-[#232323]">
            Is Warehouse Request
          </label>
        </div>
        {!isWarehouse && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
            <div className="relative">
              <div className="text-sm text-[#232323] mb-1">
                Project Associated
              </div>
              <button
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                className="w-full h-10 px-3 bg-gray-200 rounded-md text-sm text-gray-600 flex items-center justify-between hover:bg-gray-300 transition-colors"
              >
                {project.site_name || "Select Project"}
                <span className="text-[#232323]">
                  <ChevronDown size={18} />
                </span>
              </button>
              {showProjectDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {projects.length > 0 ? (
                    projects.map((proj) => (
                      <button
                        key={proj.id}
                        onClick={() => selectProject(proj)}
                        className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 flex flex-col"
                      >
                        <span className="font-medium text-gray-900">
                          {proj.site_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {proj.city || "No city"}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No projects available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delivery Address Display */}
        {(isWarehouse || project) && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Delivery Address
            </div>
            <div className="text-sm text-[#232323]">
              {(() => {
                if (isWarehouse) {
                  const org = JSON.parse(localStorage.getItem("organization")) || {};
                  const { business_address, city, state, pincode } = org;
                  if (!business_address && !city && !state && !pincode) {
                    return (
                      <span className="text-amber-600 italic">
                        Organization address currently not set, set it from organization settings later.
                      </span>
                    );
                  }
                  return (
                    <div className="flex flex-col">
                      <span className="font-medium">Warehouse / Business Address:</span>
                      <span>
                        {[business_address, city, state, pincode]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  );
                } else if (project) {
                  const { address, city, state, pincode } = project;
                  if (!address && !city && !state && !pincode) {
                    return (
                      <span className="text-amber-600 italic">
                        Site address currently not set, set it from project settings later.
                      </span>
                    );
                  }
                  return (
                    <div className="flex flex-col">
                      <span className="font-medium">Project Site Address:</span>
                      <span>
                        {[address, city, state, pincode]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center gap-2 overflow-x-auto border-b-[0.5px] px-2 pb-1 border-[#C3C3C3]">
            <div className="text-base font-medium text-gray-900">
              Materials
            </div>
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
              {materials.length}
            </span>
            <button
              onClick={addMaterial}
              className="ml-auto w-7 h-7 rounded-full border border-gray-300 bg-[#ECE6F0] text-[#3B0069] flex items-center justify-center hover:bg-gray-50"
              aria-label="Add material"
            >
              <FaPlus size={12} />
            </button>
          </div>

          <div className="mt-3 space-y-3">
            {materials.map((m, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-8 relative">
                  <button
                    // onClick={() =>
                    //   setShowMaterialDropdown(
                    //     showMaterialDropdown === idx ? null : idx
                    //   )
                    // }
                    className="w-full h-10 px-3 bg-gray-200 rounded-md flex items-center justify-between text-sm text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{m.name}</span>
                      {m.specification && m.subcategory && (
                        <span className="text-xs text-gray-500">
                          {m.specification} | {m.subcategory} | {m.brand}
                        </span>
                      )}
                    </div>
                    {/* <ChevronDown size={16} className="text-gray-400" /> */}
                  </button>
                  {showMaterialDropdown === idx && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {materialLibrary.length > 0 ? (
                        materialLibrary
                          .filter(
                            (material) =>
                              !materials.some(
                                (m, i) =>
                                  i !== idx && m.materialId === material.id,
                              ), // Exclude already selected materials
                          )
                          .map((material) => (
                            <button
                              key={material.id}
                              onClick={() => selectMaterial(idx, material)}
                              className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 flex flex-col"
                            >
                              <span className="font-medium text-gray-900">
                                {material.material_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {material.specification} |{" "}
                                {material.subcategory} |{" "}
                                {material.brand_name}
                              </span>
                            </button>
                          ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No materials available
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-4 md:col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={m.qty}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val < 0) val = 0;
                      updateMaterial(idx, { qty: val })
                    }}
                    className="w-full h-10 px-3 bg-gray-200 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <div className="h-10 px-3 bg-gray-200 rounded-md flex items-center text-xs text-[#232323]">
                    {m.unit}
                  </div>
                </div>
                <div className="col-span-2 md:col-span-1 flex items-center">
                  <button
                    onClick={() => removeMaterial(idx)}
                    className="w-8 h-8 text-red-500 hover:bg-red-50 bg-[#F9E7E7] rounded-full flex items-center justify-center transition-colors"
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div className="flex-shrink-0 p-4">
        <div className="flex items-center justify-center">
          <button
            onClick={raiseRFQ}
            className="min-w-[180px] h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#3B0069] text-white text-sm font-medium px-6 hover:bg-[#4A0082] transition-colors"
          >
            Raise RFQ
            <span className="text-white/90">
              <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            </span>
          </button>
        </div>
      </div>

      {/* Material selection modal (adds multiple materials) */}
      {showMaterialModal && (
        <MaterialSelectionModal
          isOpen={showMaterialModal}
          onClose={() => setShowMaterialModal(false)}
          onSelectMaterial={(mat) => {
            handleSelectMaterial(mat);
          }}
          selectedMaterials={materials.map((m) => ({ id: m.materialId }))}
        />
      )}
    </div>
  );
}
